import { useState } from 'react';
import { useAdminAuth } from '@/context/AdminAuthContext';
import { Navigate } from 'react-router-dom';
import { Lock, ArrowRight } from 'lucide-react';

export default function AdminLogin() {
  const { admin, loading, login } = useAdminAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  if (loading) return <div className="min-h-screen bg-[#0C4A8C] flex items-center justify-center"><p className="text-white/40 font-sans text-sm">Loading...</p></div>;
  if (admin) return <Navigate to="/admin/dashboard" replace />;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      await login(email, password);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Login failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0C4A8C] flex items-center justify-center px-4">
      <div className="w-full max-w-sm">
        <div className="flex flex-col items-center mb-8">
          <div className="flex items-center mb-4" data-testid="admin-logo">
            <span className="font-serif font-bold text-[28px] leading-none tracking-[0.04em]">
              <span className="text-white">TRU</span>
              <span style={{ color: 'rgba(255,255,255,0.45)' }}>FORM</span>
            </span>
            <span className="mx-2 h-[30px] w-px" style={{ background: 'rgba(255,255,255,0.3)' }} />
            <span className="flex flex-col items-center font-sans text-[8px] font-medium tracking-[0.25em] leading-[1.3]" style={{ color: 'rgba(255,255,255,0.45)' }}>
              <span>M</span><span>E</span><span>N</span>
            </span>
          </div>
          <div className="w-9 h-9 rounded-[2px] flex items-center justify-center mb-3" style={{ background: 'rgba(186,230,253,0.12)' }}>
            <Lock size={16} className="text-[#BAE6FD]" />
          </div>
          <h1 data-testid="admin-login-title" className="font-serif text-xl text-white font-medium">Admin Dashboard</h1>
          <p className="text-[12px] text-white/30 font-sans mt-1">Sign in to manage your products</p>
        </div>

        <form onSubmit={handleSubmit} className="bg-white rounded-[2px] p-6" data-testid="admin-login-form">
          {error && (
            <div data-testid="login-error" className="bg-red-50 border border-red-200 text-red-700 text-[12px] font-sans px-3 py-2 rounded-[2px] mb-4">
              {error}
            </div>
          )}

          <div className="mb-4">
            <label className="block text-[11px] font-sans font-medium uppercase tracking-[2px] text-[#0C4A8C]/60 mb-2">Email</label>
            <input
              data-testid="login-email-input"
              type="email" value={email} onChange={e => setEmail(e.target.value)} required
              className="w-full px-3 py-2.5 border rounded-[2px] text-sm font-sans text-[#0C4A8C] focus:outline-none focus:border-[#0C4A8C] transition-colors" style={{ borderColor: 'rgba(12,74,140,0.12)' }}
              placeholder="admin@truformmen.com"
            />
          </div>

          <div className="mb-5">
            <label className="block text-[11px] font-sans font-medium uppercase tracking-[2px] text-[#0C4A8C]/60 mb-2">Password</label>
            <input
              data-testid="login-password-input"
              type="password" value={password} onChange={e => setPassword(e.target.value)} required
              className="w-full px-3 py-2.5 border rounded-[2px] text-sm font-sans text-[#0C4A8C] focus:outline-none focus:border-[#0C4A8C] transition-colors" style={{ borderColor: 'rgba(12,74,140,0.12)' }}
              placeholder="Enter password"
            />
          </div>

          <button
            data-testid="login-submit-button"
            type="submit" disabled={submitting}
            className="w-full bg-[#0C4A8C] text-[#F0F9FF] py-3 rounded-[2px] text-[11px] font-sans font-medium uppercase tracking-[3px] hover:bg-[#093B70] transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
          >
            {submitting ? 'Signing in...' : <>Sign In <ArrowRight size={14} /></>}
          </button>
        </form>

        <p className="text-center text-[11px] text-white/20 font-sans mt-6">TRUFORM MEN Admin Portal</p>
      </div>
    </div>
  );
}
